import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Icon, Grid } from "@mui/material";
import PropTypes from "prop-types";

import InspectionTimeline from "modules/inspections/components/InspectionTimeline";
import SoftBox from "components/SoftComponents/SoftBox";
import { wrapper } from "utils/wrapper";
import { useEffect, useMemo, useState } from "react";
import InspectionPreviewCard from "modules/inspections/components/InspectionPreviewCard";

import inspectionAPI from "modules/inspections/api";
import reportAPI from "services/api/reports";

import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { useLocation } from "react-router-dom";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";
import SprayCan from "assets/images/spray_can_white.png";
import BrokenDevice from "assets/images/broken_device_white.png";

import moment from "moment-timezone";
import fileDownload from "js-file-download";
import SoftSelect from "components/SoftComponents/SoftSelect";

import { conditionConfig, conditionOptions, actionOptions, visitTypeOptions, getLastMonthRange } from "utils/inspection";

function AllInspectionsView({ map, user }) {
    inspectionAPI.loadToken(useAuth0());
    reportAPI.loadToken(useAuth0());

    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, appearance, userPermissions } = controller;

    const location = useLocation();
    const stateFilter = location?.state?.filter;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    const [selectedInspection, setSelectedInspection] = useState(null);
    const [visitTypeFilter, setVisitTypeFilter] = useState("all");

    const viewUnresolved = userPermissions.includes("operations:unresolved");
    const showUnderReview = userPermissions.includes("operations:reviewer");
    const factoryAcceptanceTester = userPermissions.includes("operations:fat");
    const canCreateReport = user.user.user_type == "developer";

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsForUser', user, selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsForUser(user);
        }
    });

    const maintenance_tags = useQuery({
        queryKey: ['getAllMaintenanceTags', selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllMaintenanceTags()
        }
    });

    const inspectionsData = all_inspections?.data?.inspections.filter(i => factoryAcceptanceTester || i.recordType == "inspection") || [];
    const maintenanceTags = maintenance_tags?.data?.filter(t => t.status == 'complete') || [];

    const maintenanceTagOptions = useMemo(() => {
        return maintenanceTags.map(m => ({ label: m.tag_label, value: m.tag_label }));
    }, [maintenanceTags]);

    const [statusFilter, setStatusFilter] = useState(() => {
        if (stateFilter) return stateFilter;

        if (viewUnresolved) {
            if (inspectionsData.some(row => row.status === "unresolved")) {
                return "unresolved";
            }
            else if (showUnderReview && inspectionsData.some(row => row.status === "review")) {
                return "review";
            }
        }

        return "all";
    });

    const visitOpts = visitTypeOptions.filter(vt => !(vt.value == 'routine' && !maintenanceTags.length))
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);

    const [reportData, setReportData] = useState(() => {
        const lastMonth = getLastMonthRange();

        return {
            visitType: "callout",
            maintenanceTag: null,
            startDate: lastMonth.start,
            endDate: lastMonth.end,
            conditions: [...conditionOptions],
            actions: [...actionOptions]
        };
    });

    useEffect(() => {
        if (maintenanceTagOptions.length > 0 && !reportData.maintenanceTag) {
            setReportData({ ...reportData, maintenanceTag: maintenanceTagOptions[maintenanceTagOptions.length - 1].value });
        }
    }, [maintenanceTagOptions]);

    const handleConditionToggle = (condition) => {
        setReportData(prev => ({
            ...prev,
            conditions: prev.conditions.includes(condition)
                ? prev.conditions.filter(c => c !== condition)
                : [...prev.conditions, condition]
        }));
    };

    const handleActionToggle = (action) => {
        setReportData(prev => ({
            ...prev,
            actions: prev.actions.includes(action)
                ? prev.actions.filter(a => a !== action)
                : [...prev.actions, action]
        }));
    };

    const generateReportMutation = useMutation({
        mutationFn: async (payload) => {
            // console.log(payload)
            return await reportAPI.generateMonthlyInspectionReport(payload.start_time, payload.end_time, payload, { responseType: 'blob' });
        },
        onSuccess: (data, variables) => {
            // variables contains the payload you passed to mutate()

            // Format the dates for a clean filename (e.g. 01082026_to_31082026)
            const formattedStart = moment(variables.start_time).format('DDMMYYYY');
            const formattedEnd = moment(variables.end_time).format('DDMMYYYY');

            var fileName = `InspectionReport_${formattedStart}-${formattedEnd}.pdf`;
            if (variables.visitType == "callout") {
                var fileName = `CalloutReport_${formattedStart}-${formattedEnd}.pdf`;
            }
            else if (variables.visitType == "routine") {
                var fileName = `MaintenanceReport_${variables.maintenanceTag.replace(" ", "-")}.pdf`;
            }

            // Trigger the browser to download the blob
            fileDownload(data, fileName);
            setIsReportModalOpen(false);
        },
        onError: (error) => {
            console.error("Failed to generate report:", error);
            // Optionally handle error UI here (e.g., SweetAlert or toast)
        }
    });

    const handleGenerateReport = () => {
        // Convert the date strings to epoch (milliseconds) before passing them to the mutation
        var payload = { ...reportData };

        if (reportData.visitType == "routine") {
            payload.start_time = new Date(maintenanceTags.find(m => m.tag_label == reportData.maintenanceTag).createdAt).getTime();
            payload.end_time = new Date(maintenanceTags.find(m => m.tag_label == reportData.maintenanceTag).completedAt).getTime();
        }
        else {
            payload.start_time = new Date(reportData.startDate).getTime();
            payload.end_time = new Date(reportData.endDate).getTime();
        }

        generateReportMutation.mutate(payload);
    };

    // Reusable styling for the option boxes
    const optionBoxStyles = (isChecked) => ({
        display: 'flex',
        alignItems: 'center',
        border: '1px solid',
        borderColor: isChecked ? 'info.main' : 'grey.300',
        backgroundColor: isChecked ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        userSelect: 'none',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        '&:hover': {
            backgroundColor: isChecked ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0, 0, 0, 0.03)',
        }
    });

    return (
        <>
            {wrapper((
                <SoftBox>
                    {deviceLayout == "mobile" ? (
                        <SoftBox borderRadius="20px" color={"white"} width="100%" height={'calc(100svh - 4.25rem - 8px)'} minHeight={"calc(100svh - 4.25rem - 8px)"}
                            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                        >
                            {selectedInspection != null ? (
                                <Box m={1} sx={{ height: deviceLayout == 'mobile' ? 'calc(100% - 16px)' : '100%' }}>
                                    <InspectionPreviewCard type={'inspection'} {...{ user, selectedInspection, setSelectedInspection }} />
                                </Box>
                            ) : (
                                <SoftBox m={deviceLayout == 'mobile' ? 1 : 0} sx={{ overflowY: 'hidden', borderRadius: '10px', height: deviceLayout == 'mobile' ? 'calc(100% - 16px)' : "100%" }}>
                                    <InspectionTimeline
                                        type={'inspection'}
                                        isLoading={all_inspections.isLoading || maintenance_tags.isLoading}
                                        inspections_data={inspectionsData}
                                        {...{ user, selectedInspection, setSelectedInspection, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter, setIsReportModalOpen, canCreateReport }}
                                    />
                                </SoftBox>
                            )}
                        </SoftBox>

                    ) : (
                        <SoftBox borderRadius="20px" color={"white"} width="100%" height={"calc(100svh - 5.5rem - 16px)"}
                            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
                        >
                            <SoftBox m={1} sx={{ overflowY: 'hidden', borderRadius: '10px', height: 'calc(100% - 16px)' }}>
                                <InspectionTimeline
                                    type={'inspection'}
                                    isLoading={all_inspections.isLoading || maintenance_tags.isLoading}
                                    inspections_data={inspectionsData}
                                    {...{ user, selectedInspection, setSelectedInspection, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter, setIsReportModalOpen, canCreateReport }}
                                >
                                    <InspectionPreviewCard type={'inspection'} {...{ user, selectedInspection, setSelectedInspection }} />
                                </InspectionTimeline>
                            </SoftBox>
                        </SoftBox>
                    )}
                </SoftBox>
            ), controller)}

            <Dialog open={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '1rem' } }}>
                <DialogTitle>
                    <SoftTypography variant="h5" fontWeight="bold">
                        Generate Inspection Report
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text">
                        Configure the parameters below to export a summary PDF.
                    </SoftTypography>
                </DialogTitle>

                <DialogContent sx={{ overflowY: reportData.visitType == "routine" ? "unset" : "auto" }}>
                    <SoftBox pt={1} pb={3} display="flex" gap={2}>
                        <SoftBox flex={1}>
                            <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">Visit Type</SoftTypography>
                            <SoftSelect
                                options={visitOpts}
                                value={visitOpts.find(opt => opt.value === reportData.visitType) || visitOpts[0]}
                                onChange={(selected) => setReportData({ ...reportData, visitType: selected.value })}
                                isDisabled={generateReportMutation.isPending}
                            />
                        </SoftBox>
                    </SoftBox>

                    {reportData.visitType == "routine" ? (
                        <SoftBox pt={1} pb={3} display="flex" gap={2}>
                            <SoftBox flex={1}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">Maintenance Identifier</SoftTypography>
                                <SoftSelect
                                    options={maintenanceTagOptions}
                                    value={maintenanceTagOptions.find(opt => opt.value === reportData.maintenanceTag) || maintenanceTagOptions[(maintenanceTagOptions.length - 1)]}
                                    onChange={(selected) => setReportData({ ...reportData, maintenanceTag: selected.value })}
                                    isDisabled={generateReportMutation.isPending}
                                />
                            </SoftBox>
                        </SoftBox>
                    ) : (
                        <>
                            <SoftBox pt={1} pb={3} display="flex" gap={2}>
                                <SoftBox flex={1}>
                                    <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">Start Date</SoftTypography>
                                    <SoftInput
                                        type="date"
                                        value={reportData.startDate}
                                        onChange={(e) => setReportData({ ...reportData, startDate: e.target.value })}
                                        disabled={generateReportMutation.isPending}
                                    />
                                </SoftBox>
                                <SoftBox flex={1}>
                                    <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block" color="text">End Date</SoftTypography>
                                    <SoftInput
                                        type="date"
                                        value={reportData.endDate}
                                        onChange={(e) => setReportData({ ...reportData, endDate: e.target.value })}
                                        disabled={generateReportMutation.isPending}
                                    />
                                </SoftBox>
                            </SoftBox>

                            {reportData.visitType != "callout" && (
                                <>
                                    <SoftBox pb={4}>
                                        <SoftTypography variant="caption" fontWeight="bold" mb={2} display="block" color="text">
                                            Include Conditions (Optional)
                                        </SoftTypography>
                                        {/* Material UI Grid Implementation */}
                                        <Grid container spacing={1}>
                                            {conditionOptions.map((condition) => {
                                                const isChecked = reportData.conditions.includes(condition);
                                                const badge = conditionConfig[condition];

                                                return (
                                                    <Grid item xs={6} sm={4} md={3} key={condition}>
                                                        <Box sx={optionBoxStyles(isChecked)} onClick={() => !generateReportMutation.isPending && handleConditionToggle(condition)}>
                                                            <SoftBox display="flex" alignItems="center" gap={1.5}>
                                                                {badge.icon === "SprayCan" && (
                                                                    <SoftBox component="img" src={SprayCan} variant="gradient" bgColor={badge.color} color="white" sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                                )}
                                                                {badge.icon === 'BrokenDevice' && (
                                                                    <SoftBox component="img" src={BrokenDevice} variant="gradient" bgColor={badge.color} color="white" sx={{ borderRadius: '4px', width: '24px', height: '24px' }} />
                                                                )}
                                                                {(badge.icon !== "BrokenDevice" && badge.icon !== "SprayCan") && (
                                                                    <SoftBox variant="gradient" bgColor={badge.color} color="white" sx={{ borderRadius: '4px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <Icon fontSize="22px !important" sx={{ color: "#fff", fontSize: '18px !important' }}>{badge.icon}</Icon>
                                                                    </SoftBox>
                                                                )}
                                                                <SoftTypography variant="button" fontWeight={isChecked ? "bold" : "regular"} textTransform="capitalize">
                                                                    {condition}
                                                                </SoftTypography>
                                                            </SoftBox>
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </SoftBox>

                                    <SoftBox pb={1}>
                                        <SoftTypography variant="caption" fontWeight="bold" mb={2} display="block" color="text">
                                            Include Actions (Optional)
                                        </SoftTypography>
                                        {/* Material UI Grid Implementation */}
                                        <Grid container spacing={1}>
                                            {actionOptions.map((action) => {
                                                const isChecked = reportData.actions.includes(action);
                                                return (
                                                    <Grid item xs={6} sm={4} md={3} key={action}>
                                                        <Box sx={optionBoxStyles(isChecked)} onClick={() => !generateReportMutation.isPending && handleActionToggle(action)}>
                                                            <SoftBox display="flex" alignItems="center" height="24px" px={0.5}>
                                                                <SoftTypography variant="button" fontWeight={isChecked ? "bold" : "regular"} textTransform="capitalize">
                                                                    {action}
                                                                </SoftTypography>
                                                            </SoftBox>
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </SoftBox>
                                </>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <SoftButton onClick={() => setIsReportModalOpen(false)} variant="text" color="secondary" disabled={generateReportMutation.isPending}>
                        Cancel
                    </SoftButton>
                    <SoftButton onClick={handleGenerateReport} variant="gradient" color="info" disabled={generateReportMutation.isPending}>
                        {generateReportMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Generate PDF'}
                    </SoftButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

AllInspectionsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default AllInspectionsView;